/**
 * @fileoverview Shared recharts renderer for a ChartSpec. Used by the inline
 * chat ChartCard and the canvas ChartNode so both render identically.
 */
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  Area,
  AreaChart,
  Pie,
  PieChart,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { ChartSpec } from "@cvx/lib/charts/spec";

const PALETTE = [
  "#2f7d4f",
  "#3b82f6",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
];

export function ChartRenderer({
  spec,
  height = 260,
  animated = true,
}: {
  spec: ChartSpec;
  height?: number;
  /**
   * Disable recharts entry animation. The PDF export renders the chart
   * offscreen and captures it with html2canvas — animation must be off
   * or the capture grabs a half-drawn frame.
   */
  animated?: boolean;
}) {
  const common = { data: spec.data };
  return (
    <ResponsiveContainer width="100%" height={height}>
      {spec.chartType === "bar" ? (
        <BarChart {...common}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {spec.series.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={PALETTE[i % PALETTE.length]}
              stackId={spec.stacked ? "a" : undefined}
              isAnimationActive={animated}
            />
          ))}
        </BarChart>
      ) : spec.chartType === "line" ? (
        <LineChart {...common}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {spec.series.map((s, i) => (
            <Line
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stroke={PALETTE[i % PALETTE.length]}
              dot={false}
              isAnimationActive={animated}
            />
          ))}
        </LineChart>
      ) : spec.chartType === "area" ? (
        <AreaChart {...common}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {spec.series.map((s, i) => (
            <Area
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stroke={PALETTE[i % PALETTE.length]}
              fill={PALETTE[i % PALETTE.length]}
              stackId={spec.stacked ? "a" : undefined}
              fillOpacity={0.2}
              isAnimationActive={animated}
            />
          ))}
        </AreaChart>
      ) : (
        <PieChart>
          <Tooltip />
          <Legend />
          <Pie
            data={spec.data}
            dataKey={spec.series[0].key}
            nameKey={spec.xKey}
            cx="50%"
            cy="50%"
            outerRadius={90}
            label
            isAnimationActive={animated}
          >
            {spec.data.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
        </PieChart>
      )}
    </ResponsiveContainer>
  );
}
